import {
	Context,
	ContextContent,
	ContextContentBody,
	ContextContentHeader,
	ContextTrigger,
} from "@/components/ai-elements/context";
import {
	PromptInput,
	PromptInputActionAddAttachments,
	PromptInputActionMenu,
	PromptInputActionMenuContent,
	PromptInputActionMenuTrigger,
	PromptInputBody,
	PromptInputFooter,
	PromptInputSubmit,
	PromptInputTextarea,
	PromptInputTools,
	usePromptInputAttachments,
	type PromptInputMessage,
} from "@/components/ai-elements/prompt-input";
import { Button } from "@/components/ui/button";
import { PaperclipIcon, XIcon } from "lucide-react";
import { useEffect, useId, useState } from "react";
import { cn } from "./cn.js";
import { type ChatModelId } from "./chat-model.js";

export type { ChatModelId } from "./chat-model.js";

function AttachmentStrip() {
	const attachments = usePromptInputAttachments();
	if (attachments.files.length === 0) return null;
	return (
		<div className="mc-attach-strip">
			{attachments.files.map((file) => (
				<div key={file.id} className="mc-attach-chip">
					{file.mediaType?.startsWith("image/") && file.url ? (
						<img
							src={file.url}
							alt=""
							className="mc-attach-thumb"
							width={36}
							height={36}
						/>
					) : (
						<span className="mc-attach-file" aria-hidden>
							<PaperclipIcon className="size-3.5" />
						</span>
					)}
					<span className="mc-attach-name truncate">
						{file.filename ?? "File"}
					</span>
					<Button
						type="button"
						variant="ghost"
						size="icon-sm"
						className="size-6 shrink-0"
						aria-label={`Remove ${file.filename ?? "file"}`}
						onClick={() => attachments.remove(file.id)}
					>
						<XIcon className="size-3" />
					</Button>
				</div>
			))}
		</div>
	);
}

type Props = {
	variant: "home" | "dock";
	draft: string;
	typing: boolean;
	/** Remount/focus when this changes (new chat id). */
	focusKey: string;
	autoFocus?: boolean;
	/** Approximate tokens used this session (stub until metering is live). */
	usedTokens?: number;
	maxTokens?: number;
	modelId: ChatModelId;
	onDraftChange: (value: string) => void;
	onSubmit: (
		text: string,
		meta: {
			modelId: ChatModelId;
			files: Array<{
				mediaType?: string;
				url?: string;
				filename?: string;
			}>;
		},
	) => void;
};

/** Chat box — attach files, type, send. Mentions and connectors live elsewhere. */
export function MairkoComposer({
	variant,
	draft,
	typing,
	focusKey,
	autoFocus = false,
	usedTokens = 0,
	maxTokens = 200_000,
	modelId,
	onDraftChange,
	onSubmit,
}: Props) {
	const labelId = useId();
	const [hasFiles, setHasFiles] = useState(false);

	return (
		<div
			className={cn(
				"mc-composer-wrap",
				variant === "home" && "mc-composer-wrap--home",
			)}
		>
			<PromptInput
				key={focusKey}
				className={cn("mc-composer", variant === "home" && "mc-composer--home")}
				accept="image/*,.pdf,.txt,.md,.csv"
				multiple
				globalDrop={variant === "home"}
				onSubmit={(message: PromptInputMessage) => {
					const text = message.text.trim();
					if (!text && message.files.length === 0) return;
					onSubmit(text || "What do you see in this attachment?", {
						modelId,
						files: message.files.map((file) => ({
							mediaType: file.mediaType,
							url: file.url,
							filename: file.filename,
						})),
					});
				}}
			>
				<ComposerFileWatcher onChange={setHasFiles} />
				<PromptInputBody>
					<AttachmentStrip />
					<PromptInputTextarea
						name="message"
						value={draft}
						onChange={(event) => onDraftChange(event.target.value)}
						placeholder="Ask anything"
						disabled={typing}
						autoFocus={autoFocus}
						className="mc-composer-input"
						aria-labelledby={labelId}
					/>
				</PromptInputBody>
				<PromptInputFooter className="mc-composer-footer">
					<PromptInputTools className="mc-composer-tools">
						<PromptInputActionMenu>
							<PromptInputActionMenuTrigger
								tooltip="Add photos or files"
								disabled={typing}
							/>
							<PromptInputActionMenuContent>
								<PromptInputActionAddAttachments />
							</PromptInputActionMenuContent>
						</PromptInputActionMenu>
					</PromptInputTools>

					<div className="mc-composer-right">
						{variant === "dock" && usedTokens / maxTokens >= 0.05 ? (
							<Context
								usedTokens={usedTokens}
								maxTokens={maxTokens}
								modelId={`anthropic/claude-${modelId}`}
							>
								<ContextTrigger className="mc-context-trigger h-8 gap-1.5 px-2" />
								<ContextContent align="end" className="w-64">
									<ContextContentHeader />
									<ContextContentBody className="pt-0">
										<p className="m-0 text-xs text-muted-foreground">
											Session estimate. Live token metering wires in with
											billing.
										</p>
									</ContextContentBody>
								</ContextContent>
							</Context>
						) : null}
						<PromptInputSubmit
							disabled={typing || (!draft.trim() && !hasFiles)}
							status={typing ? "submitted" : "ready"}
						/>
					</div>
				</PromptInputFooter>
			</PromptInput>
			<span id={labelId} className="sr-only">
				Message Mairko
			</span>
		</div>
	);
}

/** Tracks attachment count so submit stays enabled for file-only sends. */
function ComposerFileWatcher({
	onChange,
}: {
	onChange: (hasFiles: boolean) => void;
}) {
	const attachments = usePromptInputAttachments();
	useEffect(() => {
		onChange(attachments.files.length > 0);
	}, [attachments.files.length, onChange]);
	return null;
}
